import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Users, Wallet, Check, Loader2, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import i18next from 'i18next';
import api from '../../services/api';
import OptionToggle from './OptionToggle';
import TreeCategoryAutocomplete from './TreeCategoryAutocomplete';
import type { Category } from '../../types';

interface CategoryOperation {
  id: string;
  customLabel: string | null;
  amount: number | string;
  operationDate: string;
  operationType: 'credit' | 'debit';
  source: 'shared' | 'personal';
  bankAccountName: string | null;
}

interface CategoryOperationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string;
  categoryName: string;
  categoryType: 'income' | 'expense';
  isSubcategory: boolean;
  onReassigned?: () => void;
}

export default function CategoryOperationsModal({
  isOpen,
  onClose,
  categoryId,
  categoryName,
  categoryType,
  isSubcategory,
  onReassigned,
}: CategoryOperationsModalProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');

  const [operations, setOperations] = useState<CategoryOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showReassign, setShowReassign] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [targetCategoryId, setTargetCategoryId] = useState('');
  const [targetSubcategoryId, setTargetSubcategoryId] = useState('');

  const fetchOperations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/categories/${categoryType}/${categoryId}/operations`, {
        params: isSubcategory ? { subcategory: 'true' } : {},
      });
      if (res.data.success) {
        setOperations(res.data.data);
      }
    } catch {
      // Handle error
    } finally {
      setLoading(false);
    }
  }, [categoryId, categoryType, isSubcategory]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get(`/categories/${categoryType}`);
      if (res.data.success) {
        setCategories(res.data.data);
      }
    } catch {
      // Handle error
    }
  }, [categoryType]);

  useEffect(() => {
    if (isOpen) {
      fetchOperations();
      fetchCategories();
      setSelectedIds(new Set());
      setShowReassign(false);
      setTargetCategoryId('');
      setTargetSubcategoryId('');
    }
  }, [isOpen, fetchOperations, fetchCategories]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === operations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(operations.map((op) => op.id)));
    }
  };

  const handleReassign = async (all: boolean) => {
    if (!targetCategoryId) return;
    setReassigning(true);
    try {
      const body: Record<string, unknown> = {
        targetCategoryId,
        targetSubcategoryId: targetSubcategoryId || null,
        isSubcategory,
      };
      if (!all) {
        body.operationIds = Array.from(selectedIds);
      }
      await api.put(`/categories/${categoryType}/${categoryId}/reassign`, body);
      await fetchOperations();
      setSelectedIds(new Set());
      setShowReassign(false);
      setTargetCategoryId('');
      setTargetSubcategoryId('');
      onReassigned?.();
    } catch {
      // Handle error
    } finally {
      setReassigning(false);
    }
  };

  if (!isOpen) return null;

  const dateLocale = i18next.language === 'fr' ? fr : enUS;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('categories.operationsUsing', { name: categoryName })}
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
            </div>
          ) : operations.length === 0 ? (
            <p className="text-center text-gray-500 py-12">{t('categories.noOperationsFound')}</p>
          ) : (
            <>
              {/* Select all / Deselect all */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500">
                  {operations.length} {tc('operations', { count: operations.length })}
                </span>
                <button
                  onClick={toggleSelectAll}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  {selectedIds.size === operations.length
                    ? t('categories.deselectAll')
                    : t('categories.selectAll')}
                </button>
              </div>

              {/* Operations list */}
              <div className="space-y-1">
                {operations.map((op) => (
                  <OptionToggle
                    key={op.id}
                    variant="row"
                    selected={selectedIds.has(op.id)}
                    onToggle={() => toggleSelect(op.id)}
                  >
                    <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {op.customLabel || '-'}
                        </span>
                        <span title={op.source === 'shared' ? t('categories.sourceShared') : t('categories.sourcePersonal')}>
                          {op.source === 'shared' ? (
                            <Users className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                          ) : (
                            <Wallet className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{format(new Date(op.operationDate), 'dd MMM yyyy', { locale: dateLocale })}</span>
                        {op.bankAccountName && (
                          <>
                            <span>·</span>
                            <span>{op.bankAccountName}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className={`text-sm font-semibold flex-shrink-0 ${
                      op.operationType === 'credit' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {op.operationType === 'credit' ? '+' : '-'}
                      {Number(op.amount).toFixed(2)} €
                    </span>
                    </div>
                  </OptionToggle>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer - Reassign actions */}
        {operations.length > 0 && (
          <div className="border-t border-gray-200 p-4 space-y-3">
            {showReassign ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">{t('categories.reassignTarget')}</span>
                </div>
                <TreeCategoryAutocomplete
                  categories={categories}
                  categoryId={targetCategoryId}
                  subcategoryId={targetSubcategoryId}
                  onChange={(catId, subId) => {
                    setTargetCategoryId(catId);
                    setTargetSubcategoryId(subId);
                  }}
                  categoryType={categoryType}
                  placeholder={t('categories.reassignTarget')}
                />
                <div className="flex items-center gap-2 justify-end">
                  <button
                    onClick={() => setShowReassign(false)}
                    className="px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 font-medium"
                  >
                    {tc('cancel')}
                  </button>
                  {selectedIds.size > 0 && (
                    <button
                      onClick={() => handleReassign(false)}
                      disabled={!targetCategoryId || reassigning}
                      className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {reassigning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      {t('categories.reassignSelected', { count: selectedIds.size })}
                    </button>
                  )}
                  <button
                    onClick={() => handleReassign(true)}
                    disabled={!targetCategoryId || reassigning}
                    className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {reassigning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    {t('categories.reassignAll')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowReassign(true)}
                  className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
                >
                  {t('categories.reassign')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
